import React, { useEffect, useState, useMemo, memo } from "react";
import { FiEye, FiEdit3, FiTrash2, FiSave, FiPlus } from "react-icons/fi";
import axios from "axios";
import Table from "../shared/table/Table";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeaderSetting from "../shared/pageHeader/PageHeaderSetting";
import PerfectScrollbar from "react-perfect-scrollbar";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import Footer from "../shared/Footer";
import { useDropzone } from "react-dropzone";
import { Link } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const IconDropzone = ({ onUpload, index, uploadedFileName }) => {
  const [localFileName, setLocalFileName] = useState(uploadedFileName || "");
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue = "") => translations[key] || defaultValue;

  useEffect(() => {
    setLocalFileName(uploadedFileName || "");
  }, [uploadedFileName]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/*": [] },
    maxFiles: 1,
    onDrop: async (acceptedFiles) => {
      const formData = new FormData();
      formData.append("icon", acceptedFiles[0]);

      try {
        const res = await axios.post(
          `${BASE_URL}/api/fileType/upload-icon`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        const fileName = res.data.fileName;
        setLocalFileName(fileName);
        onUpload(index, fileName);
        toast.success("İkon yüklendi!");
      } catch (err) {
        console.error("❌ İkon yüklenemedi:", err);
        toast.error("İkon yüklenemedi!");
      }
    },
  });

  const handleRemove = () => {
    setLocalFileName("");
    onUpload(index, "");
  };

  return (
    <div
      {...getRootProps({
        className: "dropzone",
        style: {
          border: "2px dashed #007bff",
          padding: "15px",
          textAlign: "center",
          cursor: "pointer",
          borderRadius: "8px",
        },
      })}
    >
      <input {...getInputProps()} />
      {!localFileName ? (
        <p>{getText("LABEL_IMAGE_DROPDOWN")}</p>
      ) : (
        <>
          <img
            src={`${BASE_URL}/cdn/fileType_icon/${localFileName}`}
            alt="icon-preview"
            style={{
              width: 60,
              height: 60,
              objectFit: "contain",
              marginBottom: "10px",
            }}
          />
          <br />
          <button
            type="button"
            className="btn btn-sm btn-danger mt-2"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
          >
            {getText("DELETE")}
          </button>
        </>
      )}
    </div>
  );
};

// 📌 **Status Toggle Bileşeni**
const StatusToggle = memo(({ fileTypeId, currentStatus, fetchFileTypes }) => {
  const [isChecked, setIsChecked] = useState(currentStatus === "active");

  const toggleStatus = async () => {
    const newStatus = isChecked ? "inactive" : "active";
    setIsChecked(!isChecked); // UI'yi anında güncelle

    try {
      await axios.put(`${BASE_URL}/api/fileType/update-status/${fileTypeId}`, {
        status: newStatus,
      });
      toast.success(
        `Durum ${newStatus === "active" ? "Aktif" : "Pasif"} olarak güncellendi!`,
      );
      fetchFileTypes(); // Veriyi yeniden çek
    } catch (error) {
      console.error("❌ Durum güncellenemedi:", error);
      setIsChecked(isChecked); // Hata olursa geri al
    }
  };

  return (
    <div className="form-check form-switch text-end d-flex align-items-end justify-content-end">
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggleStatus}
        style={{
          cursor: "pointer",
          width: "45px",
          height: "22px",
          transition: "0.3s ease-in-out",
        }}
      />
    </div>
  );
});

const SettingsUzantiForm = () => {
  const [fileTypes, setFileTypes] = useState([]);
  const [newRows, setNewRows] = useState([
    { icon: "", type: "", extension: "", status: "active" },
  ]);

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue = "") => translations[key] || defaultValue;

  const isTranslationsReady = Object.keys(translations).length > 0;

  useEffect(() => {
    fetchFileTypes();
  }, []);

  // 📌 API'den verileri çek
  const fetchFileTypes = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/fileType`);
      setFileTypes(response.data);
    } catch (error) {
      console.error("❌ Dosya türleri alınamadı:", error);
      toast.error("Dosya türleri alınamadı!");
    }
  };

  const columns = useMemo(() => {
    if (!isTranslationsReady) return [];

    return [
      {
        accessorKey: "icon",
        header: getText("TABLE_ICON_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        cell: (info) => (
          <img
            src={`${process.env.REACT_APP_API_BASE_URL}/cdn/fileType_icon/${info.getValue()}`}
            alt="icon"
            style={{ width: 30, height: 30, objectFit: "contain" }}
          />
        ),
        size: 80,
      },
      {
        accessorKey: "type",
        header: getText("TABLE_TYPE_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 200,
      },
      {
        accessorKey: "extension",
        header: getText("TABLE_EXTENSION_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 150,
      },
      {
        accessorKey: "status",
        header: getText("TABLE_STATUS_LABEL"),
        meta: { headerClassName: "text-end", className: "text-end" },
        size: 120,
        cell: (info) => (
          <StatusToggle
            fileTypeId={info.row.original.id}
            currentStatus={info.getValue()}
            fetchFileTypes={fetchFileTypes}
          />
        ),
      },
      {
        accessorKey: "actions",
        header: getText("TABLE_ACTIONS_LABEL"),
        enableSorting: false,
        meta: { headerClassName: "text-end", className: "text-end" },
        size: 180,
        cell: (info) => {
          const fileTypeId = info.row.original.id;
          return (
            <div className="hstack gap-2 justify-content-end">
              <Link
                to={`/settings/filetype/edit/${fileTypeId}`}
                className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                title="Düzenle"
              >
                <FiEdit3 />
              </Link>

              <button
                className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                onClick={() => deleteFileType(fileTypeId)}
                title="Sil"
              >
                <FiTrash2 />
              </button>
            </div>
          );
        },
      },
    ];
  }, [isTranslationsReady, getText]);

  // 📌 **İşlem Fonksiyonları**
  const viewFileType = (id) => {
    toast.info("Dosya türü görüntülendi!");
  };

  const editFileType = (id) => {
    toast.success("Dosya türü düzenleniyor!");
  };

  const deleteFileType = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/fileType/${id}`);
      toast.error("Dosya türü silindi!");
      fetchFileTypes();
    } catch (error) {
      console.error("Dosya türü silinemedi:", error);
      toast.error("Dosya türü silinemedi!");
    }
  };

  const addNewRow = () => {
    setNewRows([
      ...newRows,
      { icon: "", type: "", extension: "", status: "active" },
    ]);
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...newRows];
    updated[index][field] = value;
    setNewRows(updated);
  };

  const handleIconUpload = (index, fileName) => {
    handleInputChange(index, "icon", fileName);
  };

  const saveFileTypes = async () => {
    const validRows = newRows.filter(
      (row) => row.icon.trim() && row.type.trim() && row.extension.trim(),
    );
    if (!validRows.length) {
      toast.warn(getText("WARNING_ADD_VALID_ROW"));
      return;
    }

    try {
      for (const row of validRows) {
        await axios.post(`${BASE_URL}/api/fileType`, row);
      }
      toast.success(getText("SUCCESS_FILE_TYPES_SAVED"));
      setNewRows([{ icon: "", type: "", extension: "", status: "active" }]);
      fetchFileTypes();
    } catch (error) {
      console.error(" Dosya türleri eklenemedi:", error);
      toast.error("Dosya türleri eklenemedi!");
    }
  };

  return (
    <div className="content-area setting-form">
      <ToastContainer />
      <PerfectScrollbar>
        <PageHeaderSetting />
        <div className="content-area-body">
          {/* 📌 Mevcut Dosya Türleri Listesi */}

          <div className="mb-4 d-flex align-items-center justify-content-between">
            <Table data={fileTypes} columns={columns} />
          </div>

          {/* 📌 Yeni Dosya Uzantısı Ekleme Alanı */}
          <div className="card card-body general-info">
            <h5 className="fw-bold text-primary mb-4">
              {getText("LABEL_NEW_FILE_EXTENSION")}
            </h5>
            {newRows.map((row, index) => (
              <div key={index} className="border rounded p-3 mb-3">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-bold">
                      {getText("TABLE_ICON_LABEL")}
                    </label>
                    <IconDropzone
                      onUpload={handleIconUpload}
                      index={index}
                      uploadedFileName={row.icon}
                    />

                    {row.icon && (
                      <small className="text-muted mt-2 d-block">
                        Yüklendi: <strong>{row.icon}</strong>
                      </small>
                    )}
                  </div>
                  <div className="col-lg-4">
                    <label className="form-label fw-bold">
                      {getText("TABLE_TYPE_LABEL")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={row.type}
                      onChange={(e) =>
                        handleInputChange(index, "type", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-lg-4">
                    <label className="form-label fw-bold">
                      {getText("TABLE_EXTENSION_LABEL")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={row.extension}
                      onChange={(e) =>
                        handleInputChange(index, "extension", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
            <div className="d-flex justify-content-end mb-3">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={addNewRow}
              >
                <FiPlus /> {getText("ADD_NEW_ROW")}
              </button>
            </div>
            <div className="d-flex justify-content-center">
              <button className="btn btn-primary" onClick={saveFileTypes}>
                <FiSave className="me-2" /> {getText("SAVE_BTN")}
              </button>
            </div>
          </div>
        </div>
      </PerfectScrollbar>
    </div>
  );
};

export default SettingsUzantiForm;
